'use client';

import * as React from 'react';

import { cn } from '@/utils';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

const variants = {
  primary: {
    content: 'bg-primary-500 text-white',
    arrow: 'fill-primary-500',
  },
  light: {
    content: 'bg-white text-primary-600',
    arrow: 'fill-white',
  },
  danger: {
    content: 'bg-red-500 text-white',
    arrow: 'fill-red-600',
  },
  lightPop: {
    content: 'bg-white text-white',
    arrow: 'fill-white',
  },
};

type Props = Omit<
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>,
  'content'
> &
  Omit<
    React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Root>,
    'disableHoverableContent'
  > & {
    content: React.ReactNode | string;
    variant?: 'primary' | 'light' | 'danger' | 'lightPop';
  };

const Tooltip = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  Props
>(
  (
    {
      children,
      content,
      className,
      sideOffset = 6,
      side,
      variant = 'primary',
      open,
      onOpenChange,
      defaultOpen,
      delayDuration = 500,
      ...props
    },
    ref
  ) => {
    return (
      <TooltipPrimitive.Provider>
        <TooltipPrimitive.Root
          open={open}
          defaultOpen={defaultOpen}
          onOpenChange={onOpenChange}
          delayDuration={delayDuration}
        >
          <TooltipPrimitive.Trigger asChild className="cursor-pointer">
            {children}
          </TooltipPrimitive.Trigger>
          <TooltipPrimitive.Content
            ref={ref}
            sideOffset={sideOffset}
            side={side}
            className={cn(
              'z-[1600] max-w-[384px] rounded-4 px-3 py-2 text-10 shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
              variants[variant].content,
              className
            )}
            {...props}
          >
            {content}
            <TooltipPrimitive.Arrow className={variants[variant].arrow} />
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Root>
      </TooltipPrimitive.Provider>
    );
  }
);

Tooltip.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip };
